/**
 * ChargeAndSubscribePanel — unified "charge a package and/or start a
 * subscription" cart. Replaces the two separate ChargeFromBlock and
 * SubscribeFromBlock panels on mobile so the coach can clearly see a
 * single "Charging today" total before tapping the CTA.
 *
 * Sachin spec (2026-02-26):
 *   - One block picker (shared).
 *   - Optionally pick a package (independent of sub).
 *   - Optionally pick a subscription mode (Auto-bill OR Pay upfront).
 *   - Sticky cart at bottom lists every line + Total today + ONE button.
 *   - Coach can run just a package, just a sub, or both.
 *
 * Backend: no new endpoints. Sequences `/coach/clients/{id}/charge-pack`
 * (existing) followed by `/coach/clients/{id}/blocks/{block_id}/subscribe`
 * (existing) when both are selected. If the package charge fails, the sub
 * call is skipped. If the sub fails after a successful package charge,
 * the package stays purchased (and we surface the partial outcome clearly).
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Loader2, Package, Layers, AlertTriangle, Repeat, CreditCard, Lock, X,
} from "lucide-react";
import RiskyChargeConfirmModal from "@/components/coach/RiskyChargeConfirmModal";
import CardSelector from "@/components/coach/CardSelector";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TAX_RATE_PREVIEW = 0.103; // display only; backend computes the real rate at charge time

const fmtMoney = (cents) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((cents || 0) / 100);
const shortInterval = (i) => ({ daily: "day", weekly: "wk", monthly: "mo" }[i] || i);
const longInterval = (i) => ({ daily: "day", weekly: "week", monthly: "month" }[i] || i);

export default function ChargeAndSubscribePanel({ userId, athletes = [], onChanged }) {
    const { user: me } = useAuth();
    const isOwner = me?.role === "owner";
    const COMP_MAX_SESSIONS = 20;
    const [blocks, setBlocks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedBlockId, setSelectedBlockId] = useState(null);
    const [selectedAthleteId, setSelectedAthleteId] = useState(athletes[0]?.athlete_id || "");

    // Package selection (optional).
    const [selectedPackageId, setSelectedPackageId] = useState(null);
    // Custom-price pack (2026-07-22 — "Can I still make a custom one?").
    const [customPrice, setCustomPrice] = useState("");
    const [customSessions, setCustomSessions] = useState("1");
    const [compReason, setCompReason] = useState(""); // required for $0 free grants
    const [renewMode, setRenewMode] = useState({}); // { package_id: "auto"|"manual" }
    const [splitPayment, setSplitPayment] = useState(false);
    const [sessionExpiry, setSessionExpiry] = useState("3_months"); // "3_months" | "never"
    const [waiveReactFee, setWaiveReactFee] = useState(false);
    const [reactFeePreview, setReactFeePreview] = useState(null);

    // Coach-applied discount on the package (Sachin 2026-02-26). Mode picks
    // which input is active; "none" turns the section off entirely. The
    // backend rejects sending both pct + cents > 0 simultaneously.
    const [discountMode, setDiscountMode] = useState("none"); // "none" | "percent" | "fixed"
    const [discountInput, setDiscountInput] = useState(""); // user-typed value
    const [discountReason, setDiscountReason] = useState("");

    // Family-wide / shared pool: removed 2026-02-27. Packs are ALWAYS
    // family-shared now (Sachin spec — "Subs are individual. Packages are
    // shared."). Backend force-writes family_wide=true on every new pack
    // regardless of payload; the toggle is gone from the UI.
    const multipleAthletes = athletes.length > 1;

    // Subscriptions moved to the athlete dossier (Sachin 2026-02-27 —
    // Main account / Sub-account model). This panel is **pack-only** now.
    // Subs are managed per-athlete on `/coach/athletes/:id?tab=billing`.

    const [busy, setBusy] = useState(false);
    // Per-charge card picker (Sachin 2026-06) — which saved card runs this
    // pack charge. Null → customer default. Comp ($0) grants need no card.
    const [selectedPm, setSelectedPm] = useState("");
    const [cardCount, setCardCount] = useState(null);

    // Keep athleteId in sync if the parent's athletes list refreshes.
    useEffect(() => {
        if (!selectedAthleteId && athletes.length > 0) {
            setSelectedAthleteId(athletes[0].athlete_id);
        }
    }, [athletes, selectedAthleteId]);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await axios.get(`${API}/coach/pricing-blocks`, { withCredentials: true });
                if (!alive) return;
                // Coach-side sees ALL active blocks so shorter commitments
                // can still be charged. Public surfaces filter separately.
                // Sachin 2026-02-28.
                const active = (r.data?.blocks || []).filter((b) => b.active);
                setBlocks(active);
                if (active.length > 0) setSelectedBlockId(active[0].block_id);
            } catch (e) {
                if (!alive) return;
                setError(e?.response?.data?.detail || "Couldn't load pricing blocks");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    const block = useMemo(
        () => blocks.find((b) => b.block_id === selectedBlockId) || null,
        [blocks, selectedBlockId],
    );

    // Reset selections when the block changes — they're block-scoped.
    useEffect(() => {
        setSelectedPackageId(null);
        setWaiveReactFee(false);
    }, [selectedBlockId]);

    // Probe reactivation fee preview (only renders the waive row when it'd apply).
    useEffect(() => {
        if (!block || !block.reactivation_fee_cents) { setReactFeePreview(null); return; }
        let alive = true;
        (async () => {
            try {
                const r = await axios.get(
                    `${API}/coach/clients/${userId}/reactivation-fee-preview`,
                    {
                        params: { block_id: block.block_id, athlete_id: selectedAthleteId || undefined },
                        withCredentials: true,
                    },
                );
                if (alive) setReactFeePreview(r.data);
            } catch {
                if (alive) setReactFeePreview(null);
            }
        })();
        return () => { alive = false; };
    }, [userId, block?.block_id, selectedAthleteId]);

    // ── Derived: package math ────────────────────────────────────────────
    // Non-member blocks (no enabled subscription on the block) can NEVER
    // auto-renew — there's no sub to anchor a renewal to. So we force
    // "manual" mode for all packages on these blocks, hide the Auto/Manual
    // toggle, and use the manual price tier. Coaches kept setting manual
    // prices on the Non-Member block and seeing the auto-renew label —
    // confusing and incorrect. Sachin 2026-02-27.
    const blockHasSub = !!block?.subscription?.enabled;
    const getMode = (pkg) => {
        if (!blockHasSub) return "manual";
        return renewMode[pkg.package_id] ?? (pkg.auto_renew_default === false ? "manual" : "auto");
    };
    const setMode = (pkg_id, mode) => setRenewMode((m) => ({ ...m, [pkg_id]: mode }));
    const priceForMode = (pkg, mode) => (mode === "manual" && pkg.manual_per_session_cents != null
        ? pkg.manual_per_session_cents : pkg.per_session_cents);

    const isCustomSelected = selectedPackageId === "__custom__";
    const customPriceCents = Math.round((parseFloat(customPrice) || 0) * 100);
    const customSessionCount = Math.max(1, parseInt(customSessions, 10) || 1);
    // Free / comp grant (Sachin 2026-09): custom price typed as 0 → give
    // sessions with no charge. Owner-only, reason required, session cap.
    const isComp = isCustomSelected && customPrice.trim() !== "" && customPriceCents === 0;
    const compSessionsValid = customSessionCount >= 1 && customSessionCount <= COMP_MAX_SESSIONS;
    const customValid = isComp
        ? (isOwner && compReason.trim().length > 0 && compSessionsValid)
        : (customPriceCents > 0 && customPriceCents <= 500000);

    const selectedPackage = useMemo(() => {
        if (isCustomSelected) {
            if (!customValid) return null;
            return {
                package_id: "__custom__",
                is_custom: true,
                sessions: customSessionCount,
                per_session_cents: Math.floor(customPriceCents / customSessionCount),
            };
        }
        return block?.packages?.find((p) => p.package_id === selectedPackageId) || null;
    }, [block, selectedPackageId, isCustomSelected, customValid, customSessionCount, customPriceCents]);

    const pkgLine = useMemo(() => {
        if (!selectedPackage) return null;
        const mode = selectedPackage.is_custom ? "manual" : getMode(selectedPackage);
        const perSession = priceForMode(selectedPackage, mode);
        const baseSubtotal = selectedPackage.is_custom
            ? customPriceCents
            : selectedPackage.sessions * perSession;
        // Coach-applied discount preview (Sachin 2026-02-26). Backend has the
        // final say at charge time — this is for the cart display only.
        let discountCents = 0;
        const rawInput = parseFloat(discountInput);
        if (discountMode === "percent" && !Number.isNaN(rawInput) && rawInput > 0) {
            const pct = Math.min(100, Math.max(0, rawInput));
            discountCents = Math.round(baseSubtotal * (pct / 100));
        } else if (discountMode === "fixed" && !Number.isNaN(rawInput) && rawInput > 0) {
            // FE input is in DOLLARS (typed by coach), backend takes cents.
            discountCents = Math.round(rawInput * 100);
        }
        discountCents = Math.max(0, Math.min(discountCents, baseSubtotal));
        const discountedSubtotal = baseSubtotal - discountCents;
        const tax = Math.round(discountedSubtotal * TAX_RATE_PREVIEW);
        const total = discountedSubtotal + tax;
        const reactFee = reactFeePreview?.would_apply && !waiveReactFee ? reactFeePreview.fee_cents : 0;
        return {
            label: selectedPackage.is_custom
                ? `Custom — ${selectedPackage.sessions}-session pack · Manual`
                : `${selectedPackage.sessions}-session pack · ${mode === "auto" ? "Auto-renew" : "Manual"}`,
            perSession,
            baseSubtotal,
            discountCents,
            discountKind: discountMode === "percent" ? "percent" : discountMode === "fixed" ? "fixed" : null,
            subtotal: discountedSubtotal,
            tax,
            reactFee,
            total: total + reactFee,
            mode,
            autoRenew: selectedPackage.is_custom ? false : mode === "auto",
        };
    }, [selectedPackage, renewMode, reactFeePreview, waiveReactFee, discountMode, discountInput, customPriceCents]);

    // Subscriptions moved to athlete dossier (Sachin 2026-02-27). This
    // panel is pack-only. We keep `isMemberBlock` because pack expiry
    // hangs off whether the block is a member block — derived from the
    // block's subscription config.
    const isMemberBlock = !!block?.subscription?.enabled;

    // ── Cart totals ──────────────────────────────────────────────────────
    const cartLines = [];
    if (pkgLine) cartLines.push(pkgLine);
    const cartTotal = cartLines.reduce((acc, l) => acc + l.total, 0);

    // ── Submit ───────────────────────────────────────────────────────────
    // Sachin 2026-03-06: replaced `window.confirm` with a hard modal that
    // gates $0 / big-discount charges behind a type-to-confirm token so a
    // coach can't accidentally comp a paying customer.
    const [pending, setPending] = useState(null);

    const charge = () => {
        if (busy || cartLines.length === 0) return;
        if (athletes.length > 0 && !selectedAthleteId) { toast.error("Pick an athlete first"); return; }
        if (!block) return;

        const athleteName = athletes.find((a) => a.athlete_id === selectedAthleteId)?.first_name
            || athletes.find((a) => a.athlete_id === selectedAthleteId)?.name
            || "this athlete";

        // Modal props — one line per cart entry; reactivation fee gets its
        // own row (so a coach can see it's not folded into the discount).
        const lines = [];
        if (pkgLine) {
            lines.push({
                label: pkgLine.label,
                sub: `${selectedPackage.sessions} × ${fmtMoney(pkgLine.perSession)}/session`,
                amountCents: pkgLine.baseSubtotal,
                strike: pkgLine.discountCents > 0,
            });
            if (pkgLine.discountCents > 0) {
                lines.push({ label: "− Discount", amountCents: -pkgLine.discountCents });
                lines.push({ label: "= Subtotal", amountCents: pkgLine.subtotal });
            }
            lines.push({ label: `+ Tax (~${(TAX_RATE_PREVIEW * 100).toFixed(1)}%)`, amountCents: pkgLine.tax });
            if (pkgLine.reactFee > 0) {
                lines.push({ label: "+ Reactivation fee", amountCents: pkgLine.reactFee });
            }
        }

        const modalDiscount = pkgLine && pkgLine.discountCents > 0 ? {
            kind: pkgLine.discountKind,
            value: pkgLine.discountKind === "percent"
                ? (parseFloat(discountInput) || 0)
                : (Math.round((pkgLine.discountCents || 0)) / 100),
            durationLabel: "once",
            durationKind: "one_time",
            source: "ad_hoc",
            reason: discountReason.trim() || undefined,
        } : null;

        setPending({
            athleteName,
            title: isComp ? "Grant free sessions" : (selectedPackage?.is_custom ? "Charge custom pack" : `Charge pack · ${block.name}`),
            lines,
            basePriceCents: pkgLine ? pkgLine.baseSubtotal + Math.round(pkgLine.baseSubtotal * TAX_RATE_PREVIEW) : cartTotal,
            effectivePriceCents: cartTotal,
            interval: null,
            discount: modalDiscount,
            footNote: isComp
                ? `Free grant — no charge, no card needed. Books a $0 comp entry. Reason: ${compReason.trim()}`
                : "Single charge NOW on the card on file. Tax is an estimate — backend computes the exact rate at charge time.",
        });
    };

    const commit = async () => {
        if (!pending) return;
        setPending(null);
        setBusy(true);
        try {
            // ── Step 1: charge the package (if selected) ─────────────────
            if (pkgLine && selectedPackage) {
                const subtotalCents = pkgLine.baseSubtotal;
                const totalDollars = subtotalCents / 100;
                try {
                    const r = await axios.post(
                        `${API}/coach/clients/${userId}/charge-pack`,
                        {
                            pack_key: "custom",
                            custom_price_dollars: totalDollars,
                            custom_sessions: selectedPackage.sessions,
                            custom_is_member: isMemberBlock,
                            custom_auto_renew: pkgLine.autoRenew,
                            athlete_id: selectedAthleteId || null,
                            split_payment: isComp ? false : !!splitPayment,
                            session_expiry: sessionExpiry,
                            block_id: block.block_id,
                            waive_reactivation_fee: !!waiveReactFee,
                            ...(isComp ? { comp_reason: compReason.trim() } : {}),
                            // Note (2026-02-27): `family_wide` is forced TRUE
                            // server-side now — packs are always family-shared.
                            // Coach-applied discount — backend re-validates and
                            // applies the same math we previewed in the cart.
                            ...(pkgLine.discountKind === "percent" && discountInput
                                ? { discount_pct: Math.min(100, Math.max(0, parseFloat(discountInput) || 0)) }
                                : {}),
                            ...(pkgLine.discountKind === "fixed" && pkgLine.discountCents > 0
                                ? { discount_cents: pkgLine.discountCents }
                                : {}),
                            ...(discountReason.trim() && pkgLine.discountCents > 0
                                ? { discount_reason: discountReason.trim() }
                                : {}),
                            // Per-charge card pick (skip for comp $0 grants).
                            ...(!isComp && selectedPm && selectedPm !== "demo"
                                ? { payment_method_id: selectedPm }
                                : {}),
                        },
                        { withCredentials: true },
                    );
                    const reactFee = r.data?.reactivation_fee_cents || 0;
                    const reactWaived = !!r.data?.reactivation_fee_waived;
                    if (isComp) {
                        toast.success(`Granted ${selectedPackage.sessions} free session${selectedPackage.sessions === 1 ? "" : "s"}`, {
                            description: `No charge · ${compReason.trim()}`,
                        });
                    } else {
                        toast.success(`Package charged · ${selectedPackage.sessions} sessions`, {
                            description: `${fmtMoney(r.data?.charge_amount_cents || pkgLine.total)}` + (
                                reactFee > 0 ? ` (incl. ${fmtMoney(reactFee)} reactivation fee)`
                                    : reactWaived ? ` · reactivation fee waived` : ""
                            ),
                        });
                    }
                } catch (e) {
                    const detail = e?.response?.data?.detail || e?.message;
                    const code = typeof detail === "object" ? detail?.code : e?.response?.data?.code;
                    const msg = typeof detail === "object" ? detail?.message : detail;
                    if (code === "no_payment_method") {
                        toast.error("No card on file", {
                            description: "Use the 'Enter card' button by their name (or the card panel in Billing) before charging.",
                        });
                    } else {
                        toast.error("Package charge failed", {
                            description: msg || "Unknown error — check the client's card on file and try again.",
                        });
                    }
                    return; // bail — don't fire the sub if the pack failed
                }
            }

            // Subscriptions are managed on the athlete dossier now (Sachin
            // 2026-02-27 — Main account / Sub-account model). This panel
            // only handles pack charges.

            // Reset selections after a clean success.
            setSelectedPackageId(null);
            setCustomPrice("");
            setCompReason("");
            setSessionExpiry("3_months");
            setCustomSessions("1");
            setSplitPayment(false);
            setWaiveReactFee(false);
            setDiscountMode("none");
            setDiscountInput("");
            setDiscountReason("");
            onChanged?.();
        } finally {
            setBusy(false);
        }
    };

    // ── Render ───────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="flex items-center gap-2 text-zinc-400 text-xs py-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading pricing blocks…
            </div>
        );
    }
    if (error) {
        return (
            <div className="text-xs text-amber-400 inline-flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> {error}
            </div>
        );
    }
    if (blocks.length === 0) {
        return (
            <div className="text-xs text-zinc-400">
                No active pricing blocks. Go to{" "}
                <span className="text-brand-red">Owner → Pricing Blocks</span> to set them up.
            </div>
        );
    }

    return (
        <div data-testid="charge-and-subscribe" className="space-y-4 pb-32">
            {/* 1 · Block picker */}
            <div>
                <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-500 mb-2">1 · Pick a block</div>
                <div className="flex flex-wrap gap-2">
                    {blocks.map((b) => {
                        const selected = b.block_id === selectedBlockId;
                        const isManualOnly = !b.subscription?.enabled;
                        return (
                            <button
                                key={b.block_id}
                                type="button"
                                onClick={() => setSelectedBlockId(b.block_id)}
                                data-testid={`casp-block-${b.block_id}`}
                                title={isManualOnly ? "One-off / manual charge — no auto-renew" : "Subscription block — packages renew automatically"}
                                className={`text-[11px] tracking-[0.15em] uppercase px-2.5 py-1.5 border transition-colors ${
                                    selected
                                        ? "bg-brand-red border-brand-red text-white"
                                        : "border-zinc-700 text-zinc-300 hover:border-brand-red"
                                }`}
                            >
                                <span className="inline-flex items-center gap-1.5">
                                    {b.subscription?.enabled ? <Repeat className="w-3 h-3" /> : <Package className="w-3 h-3" />}
                                    {b.name}
                                    {b.is_fallback && <Lock className="w-2.5 h-2.5 ml-0.5" />}
                                    <span
                                        data-testid={`casp-block-mode-${b.block_id}`}
                                        className={`ml-1 px-1.5 py-0.5 text-[8px] tracking-[0.2em] border ${
                                            selected
                                                ? "border-white/40 text-white/85"
                                                : isManualOnly
                                                    ? "border-amber-700/60 text-amber-300/90 bg-amber-950/30"
                                                    : "border-emerald-700/60 text-emerald-300/90 bg-emerald-950/30"
                                        }`}
                                    >
                                        {isManualOnly ? "Manual" : "Auto-renew"}
                                    </span>
                                </span>
                            </button>
                        );
                    })}
                </div>
                {block && !blockHasSub && (
                    <div
                        data-testid="casp-non-member-banner"
                        className="mt-2 inline-flex items-center gap-2 px-2.5 py-1.5 border border-amber-700/40 bg-amber-950/20 text-[10px] tracking-[0.15em] uppercase text-amber-300/90"
                    >
                        <Package className="w-3 h-3" />
                        <span>{block.name} · one-off charge · no auto-renew</span>
                    </div>
                )}
            </div>

            {/* 2 · Athlete picker */}
            {athletes.length > 1 && (
                <div>
                    <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-500 mb-2">2 · For which athlete?</div>
                    <div className="flex flex-wrap gap-2">
                        {athletes.map((a) => {
                            const selected = a.athlete_id === selectedAthleteId;
                            return (
                                <button
                                    key={a.athlete_id}
                                    type="button"
                                    onClick={() => setSelectedAthleteId(a.athlete_id)}
                                    data-testid={`casp-athlete-${a.athlete_id}`}
                                    className={`text-[11px] px-2.5 py-1 border ${
                                        selected
                                            ? "bg-zinc-800 border-zinc-600 text-white"
                                            : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
                                    }`}
                                >
                                    {a.first_name || a.name}
                                </button>
                            );
                        })}
                    </div>
                    {/* family_wide toggle removed 2026-02-27 — packs are
                        always family-shared now (Sachin spec). When 2+
                        athletes are on the account, every pack on this
                        account is automatically shared across all kids. */}
                    {multipleAthletes && (
                        <div
                            data-testid="casp-family-pool-notice"
                            className="mt-3 px-3 py-2 border border-emerald-700/30 bg-emerald-900/10 text-[10px] text-emerald-200/80 leading-snug"
                        >
                            <strong className="text-emerald-300 tracking-[0.1em] uppercase">
                                Packs are family-shared.
                            </strong>{" "}
                            Every kid on this account draws from the same pool of sessions. The athlete picker above only matters for sub-account tagging on attendance.
                        </div>
                    )}
                </div>
            )}

            {/* 3 · Package (optional) */}
            <div>
                <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-500 mb-2 flex items-center gap-2">
                    {athletes.length > 1 ? "3" : "2"} · Package <span className="text-zinc-600 normal-case tracking-normal text-[10px]">· optional</span>
                </div>
                {block?.packages?.length === 0 && (
                    <div className="text-xs text-zinc-500 italic border border-dashed border-zinc-800 px-3 py-2 mb-2">
                        No session packages on this block — use Custom below.
                    </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {block?.packages?.map((p) => {
                            const mode = getMode(p);
                            const perSession = priceForMode(p, mode);
                            const hasManual = p.manual_per_session_cents != null && p.manual_per_session_cents !== p.per_session_cents;
                            const subtotal = p.sessions * perSession;
                            const tax = Math.round(subtotal * TAX_RATE_PREVIEW);
                            const total = subtotal + tax;
                            const isSelected = selectedPackageId === p.package_id;
                            return (
                                <button
                                    key={p.package_id}
                                    type="button"
                                    onClick={() => setSelectedPackageId(isSelected ? null : p.package_id)}
                                    data-testid={`casp-pkg-${p.package_id}`}
                                    className={`text-left border transition-colors flex flex-col ${
                                        isSelected
                                            ? "border-brand-red bg-brand-red/10"
                                            : "border-zinc-800 hover:border-brand-red"
                                    }`}
                                >
                                    <div className="px-3 py-2.5 border-b border-zinc-900">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-1.5">
                                                <Layers className={`w-3.5 h-3.5 ${isSelected ? "text-brand-red" : "text-brand-red/70"}`} />
                                                <span className="text-sm text-white font-semibold">
                                                    {p.sessions} session{p.sessions === 1 ? "" : "s"}
                                                </span>
                                            </div>
                                            {isSelected && (
                                                <span className="text-[9px] tracking-[0.2em] uppercase text-brand-red font-semibold">
                                                    Selected
                                                </span>
                                            )}
                                        </div>
                                        {hasManual && blockHasSub ? (
                                            <div className="grid grid-cols-2 gap-1 mt-1.5">
                                                <span
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={(e) => { e.stopPropagation(); setMode(p.package_id, "auto"); }}
                                                    onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setMode(p.package_id, "auto"); } }}
                                                    data-testid={`casp-pkg-${p.package_id}-mode-auto`}
                                                    className={`text-[10px] tracking-[0.15em] uppercase py-1 text-center border cursor-pointer ${
                                                        mode === "auto"
                                                            ? "bg-emerald-700/30 border-emerald-600 text-emerald-200"
                                                            : "border-zinc-800 text-zinc-500 hover:border-zinc-600"
                                                    }`}
                                                >
                                                    Auto · ${(p.per_session_cents / 100).toFixed(0)}
                                                </span>
                                                <span
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={(e) => { e.stopPropagation(); setMode(p.package_id, "manual"); }}
                                                    onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setMode(p.package_id, "manual"); } }}
                                                    data-testid={`casp-pkg-${p.package_id}-mode-manual`}
                                                    className={`text-[10px] tracking-[0.15em] uppercase py-1 text-center border cursor-pointer ${
                                                        mode === "manual"
                                                            ? "bg-zinc-800 border-zinc-600 text-white"
                                                            : "border-zinc-800 text-zinc-500 hover:border-zinc-600"
                                                    }`}
                                                >
                                                    Manual · ${(p.manual_per_session_cents / 100).toFixed(0)}
                                                </span>
                                            </div>
                                        ) : (
                                            <div className="text-[10px] text-zinc-400 mt-1 font-mono">
                                                ${(perSession / 100).toFixed(2)}/sess
                                                {blockHasSub && p.auto_renew_default !== false && (
                                                    <span className="ml-1.5 text-emerald-400">· Auto-renew</span>
                                                )}
                                                {!blockHasSub && (
                                                    <span className="ml-1.5 text-zinc-500">· Manual</span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="px-3 py-2">
                                        <div className="text-[11px] font-mono text-zinc-300">
                                            {fmtMoney(subtotal)} <span className="text-zinc-500">+ tax</span>{" "}
                                            <span className="text-white">{fmtMoney(total)}</span>
                                        </div>
                                        <div className={`text-[10px] mt-0.5 ${isSelected ? "text-brand-red" : "text-zinc-500"}`}>
                                            {isSelected ? "Tap to remove" : "Tap to add to cart"}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                        {/* Custom-price tile (2026-07-22) — coach types any price + sessions */}
                        <button
                            type="button"
                            onClick={() => setSelectedPackageId(isCustomSelected ? null : "__custom__")}
                            data-testid="casp-pkg-custom"
                            className={`text-left border transition-colors flex flex-col border-dashed ${
                                isCustomSelected
                                    ? "border-brand-red bg-brand-red/10"
                                    : "border-zinc-700 hover:border-brand-red"
                            }`}
                        >
                            <div className="px-3 py-2.5 border-b border-zinc-900">
                                <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-1.5">
                                        <Layers className={`w-3.5 h-3.5 ${isCustomSelected ? "text-brand-red" : "text-brand-red/70"}`} />
                                        <span className="text-sm text-white font-semibold">Custom</span>
                                    </div>
                                    {isCustomSelected && (
                                        <span className="text-[9px] tracking-[0.2em] uppercase text-brand-red font-semibold">
                                            Selected
                                        </span>
                                    )}
                                </div>
                                <div className="text-[10px] text-zinc-400 mt-1 font-mono">
                                    type any price <span className="ml-1.5 text-zinc-500">· Manual</span>
                                </div>
                            </div>
                            <div className="px-3 py-2">
                                <div className="text-[11px] font-mono text-zinc-300">
                                    {isComp
                                        ? <span className="text-emerald-300">Free grant · no charge</span>
                                        : customValid ? <>{fmtMoney(customPriceCents)} <span className="text-zinc-500">+ tax</span></> : "You set the total"}
                                </div>
                                <div className={`text-[10px] mt-0.5 ${isCustomSelected ? "text-brand-red" : "text-zinc-500"}`}>
                                    {isCustomSelected ? "Tap to remove" : "Tap to add to cart"}
                                </div>
                            </div>
                        </button>
                    </div>

                {isCustomSelected && (
                    <div className="mt-3 flex flex-wrap items-end gap-3 border border-zinc-800 bg-zinc-950/60 px-3 py-2.5" data-testid="casp-custom-inputs">
                        <label className="block">
                            <span className="block text-[10px] tracking-[0.18em] uppercase text-zinc-500 mb-1">Total price ($)</span>
                            <input
                                type="number"
                                min="0"
                                max="5000"
                                step="0.01"
                                inputMode="decimal"
                                placeholder="0.00"
                                value={customPrice}
                                onChange={(e) => setCustomPrice(e.target.value)}
                                data-testid="casp-custom-price-input"
                                className="w-28 bg-black border border-zinc-700 px-2.5 py-1.5 text-sm font-mono text-white focus:outline-none focus:border-brand-red"
                            />
                        </label>
                        <label className="block">
                            <span className="block text-[10px] tracking-[0.18em] uppercase text-zinc-500 mb-1">Sessions</span>
                            <input
                                type="number"
                                min="1"
                                max="100"
                                step="1"
                                inputMode="numeric"
                                value={customSessions}
                                onChange={(e) => setCustomSessions(e.target.value)}
                                data-testid="casp-custom-sessions-input"
                                className="w-20 bg-black border border-zinc-700 px-2.5 py-1.5 text-sm font-mono text-white focus:outline-none focus:border-brand-red"
                            />
                        </label>
                        {!customValid && !isComp && customPrice !== "" && (
                            <span className="text-[11px] text-amber-400 pb-1.5">$0.01 – $5,000</span>
                        )}
                        {!isComp && (
                            <span className="text-[10px] text-zinc-500 pb-1.5">
                                One-off charge · no auto-renew · $0 = free grant
                            </span>
                        )}
                    </div>
                )}

                {/* Free / comp grant panel — appears when custom price is $0 */}
                {isComp && (
                    <div className="mt-3 border border-emerald-700/50 bg-emerald-950/20 px-3 py-2.5 rounded" data-testid="casp-comp-panel">
                        <div className="text-[11px] tracking-[0.15em] uppercase text-emerald-300 font-semibold mb-1">Free / comp grant</div>
                        {!isOwner ? (
                            <p className="text-xs text-amber-300" data-testid="casp-comp-owner-only">
                                Only the owner can grant free ($0) sessions.
                            </p>
                        ) : (
                            <>
                                <p className="text-[11px] text-zinc-400 mb-2 leading-snug">
                                    No card needed — gives {customSessionCount} session{customSessionCount === 1 ? "" : "s"} at no charge and logs a $0 comp entry in your books. A reason is required.
                                </p>
                                <input
                                    type="text"
                                    maxLength={200}
                                    value={compReason}
                                    onChange={(e) => setCompReason(e.target.value)}
                                    placeholder="Reason (required — e.g. makeup for cancelled camp)"
                                    data-testid="casp-comp-reason"
                                    className="w-full bg-black border border-zinc-700 text-white text-sm px-2.5 py-1.5 focus:outline-none focus:border-emerald-500"
                                />
                                {!compSessionsValid && (
                                    <span className="block mt-1.5 text-[11px] text-amber-400" data-testid="casp-comp-cap">
                                        Free grants are capped at {COMP_MAX_SESSIONS} sessions.
                                    </span>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* Session expiry — coach-selectable, defaults to 3 months */}
                {pkgLine && (
                    <label className="flex items-center gap-2 text-[11px] text-zinc-400 mt-3">
                        <span className="uppercase tracking-wide">Sessions expire</span>
                        <select
                            data-testid="casp-expiry-select"
                            value={sessionExpiry}
                            onChange={(e) => setSessionExpiry(e.target.value)}
                            className="bg-black border border-zinc-700 px-2 py-1 text-sm text-white focus:outline-none focus:border-brand-red"
                        >
                            <option value="3_months">3 months from purchase</option>
                            <option value="end_of_billing_month">End of billing month</option>
                            <option value="never">Never</option>
                        </select>
                    </label>
                )}

                {/* 50/50 split toggle — only meaningful when a package is in cart */}
                {pkgLine && !isComp && (
                    <label className="inline-flex items-center gap-2 text-[11px] text-zinc-400 cursor-pointer mt-3">
                        <input
                            type="checkbox"
                            data-testid="casp-split-toggle"
                            checked={splitPayment}
                            onChange={(e) => setSplitPayment(e.target.checked)}
                            className="accent-brand-red"
                        />
                        Split package into 50/50 (half + $2 fee now, half in 30 days)
                    </label>
                )}

                {/* Coach-applied discount (Sachin 2026-02-26) — only meaningful when a package is in cart */}
                {pkgLine && !isComp && (
                    <div data-testid="casp-discount-row" className="mt-3 border border-zinc-800 px-3 py-2.5 bg-zinc-950/60">
                        <div className="flex items-center justify-between gap-2 mb-2">
                            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-300 font-semibold">
                                Discount on package
                            </div>
                            <div className="flex items-center gap-1">
                                {["none", "percent", "fixed"].map((m) => (
                                    <button
                                        key={m}
                                        type="button"
                                        onClick={() => {
                                            setDiscountMode(m);
                                            if (m === "none") setDiscountInput("");
                                        }}
                                        data-testid={`casp-discount-mode-${m}`}
                                        className={`text-[10px] tracking-[0.15em] uppercase px-2 py-1 border transition-colors ${
                                            discountMode === m
                                                ? "bg-emerald-700/30 border-emerald-600 text-emerald-200"
                                                : "border-zinc-800 text-zinc-500 hover:border-zinc-600"
                                        }`}
                                    >
                                        {m === "none" ? "Off" : m === "percent" ? "%" : "$"}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {discountMode !== "none" && (
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <div className="relative flex-1">
                                        <input
                                            type="number"
                                            inputMode="decimal"
                                            min={0}
                                            max={discountMode === "percent" ? 100 : undefined}
                                            step={discountMode === "percent" ? 1 : 0.01}
                                            value={discountInput}
                                            onChange={(e) => setDiscountInput(e.target.value)}
                                            placeholder={discountMode === "percent" ? "10" : "20.00"}
                                            data-testid="casp-discount-input"
                                            className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm px-2.5 py-1.5 pr-7 font-mono focus:border-emerald-600 focus:outline-none"
                                        />
                                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-zinc-500 font-mono pointer-events-none">
                                            {discountMode === "percent" ? "%" : "$"}
                                        </span>
                                    </div>
                                    {pkgLine.discountCents > 0 && (
                                        <span className="text-[11px] font-mono text-emerald-300 whitespace-nowrap">
                                            −{fmtMoney(pkgLine.discountCents)}
                                        </span>
                                    )}
                                </div>
                                <input
                                    type="text"
                                    value={discountReason}
                                    onChange={(e) => setDiscountReason(e.target.value)}
                                    placeholder="Reason (optional — e.g. loyalty, sibling, friend referral)"
                                    data-testid="casp-discount-reason"
                                    maxLength={200}
                                    className="w-full bg-zinc-900 border border-zinc-800 text-white text-[11px] px-2.5 py-1.5 focus:border-emerald-600 focus:outline-none"
                                />
                                {pkgLine.discountCents > 0 && (
                                    <div className="text-[10px] text-zinc-500 leading-snug">
                                        Discount applies BEFORE tax. Base: {fmtMoney(pkgLine.baseSubtotal)} → after discount: {fmtMoney(pkgLine.subtotal)}.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Reactivation fee waive — only visible when the fee would apply */}
                {pkgLine && reactFeePreview?.would_apply && (
                    <label
                        data-testid="casp-waive-react-fee-row"
                        className={`block px-3 py-2.5 border mt-3 ${waiveReactFee ? "border-emerald-700 bg-emerald-900/15" : "border-amber-700/60 bg-amber-900/10"} cursor-pointer transition-colors`}
                    >
                        <div className="flex items-start gap-2.5">
                            <input
                                type="checkbox"
                                data-testid="casp-waive-react-fee"
                                checked={waiveReactFee}
                                onChange={(e) => setWaiveReactFee(e.target.checked)}
                                className="accent-emerald-600 mt-0.5"
                            />
                            <div className="flex-1">
                                <div className={`text-[11px] font-semibold tracking-[0.15em] uppercase ${waiveReactFee ? "text-emerald-300" : "text-amber-300"}`}>
                                    {waiveReactFee ? "Reactivation fee waived" : `Waive ${fmtMoney(reactFeePreview.fee_cents)} reactivation fee`}
                                </div>
                                <div className="text-[10px] text-zinc-400 mt-0.5 leading-snug">
                                    {waiveReactFee
                                        ? "Charge will skip the auto-applied fee. Decision logged to audit."
                                        : "Returning customer — fee applies on top of pack price unless you check this."}
                                </div>
                            </div>
                        </div>
                    </label>
                )}
            </div>

            {/* 4 · Subscription removed 2026-02-27 — subs are managed per-athlete
                on the athlete dossier. When this block has a sub configured, we
                show a pointer instead of an inline subscribe action. */}
            {block?.subscription?.enabled && (
                <div
                    data-testid="casp-sub-moved-notice"
                    className="border border-zinc-800 bg-zinc-900/40 px-3 py-2.5 text-[11px] text-zinc-300 leading-snug"
                >
                    <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1">
                        Starting a subscription?
                    </div>
                    This block has a subscription tier configured. Subscriptions are managed <strong className="text-white">per athlete</strong> now — open the athlete&apos;s dossier and click <span className="text-brand-red">Subscribe</span> from their Billing tab.
                </div>
            )}

            {/* ── Cart summary + single CTA ──────────────────────────────── */}
            <div
                data-testid="casp-cart-summary"
                style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 72px)" }}
                className="fixed inset-x-0 z-30 border-t-2 border-brand-red bg-zinc-950/95 backdrop-blur-sm px-4 py-3 shadow-[0_-8px_20px_rgba(0,0,0,0.6)] md:static md:bottom-auto md:inset-x-auto md:border md:border-brand-red/40 md:bg-zinc-950/70 md:px-4 md:py-3 md:mt-4 md:rounded md:!bottom-auto"
            >
                <div className="max-w-3xl mx-auto">
                    <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-400 mb-2">
                        Charging today
                    </div>
                    {cartLines.length === 0 ? (
                        <div className="text-xs text-zinc-500 italic mb-1">
                            Nothing selected — pick a package and/or a subscription above.
                        </div>
                    ) : (
                        <ul className="space-y-1 mb-2">
                            {cartLines.map((l, i) => (
                                <li key={i} className="flex items-baseline justify-between gap-2 text-[11px]">
                                    <span className="text-zinc-300 truncate">{l.label}</span>
                                    <span className="font-mono text-white whitespace-nowrap">{fmtMoney(l.total)}</span>
                                </li>
                            ))}
                            {pkgLine && pkgLine.discountCents > 0 && (
                                <li className="flex items-baseline justify-between gap-2 text-[10px] text-emerald-300">
                                    <span>(discount applied)</span>
                                    <span className="font-mono">−{fmtMoney(pkgLine.discountCents)}</span>
                                </li>
                            )}
                            {pkgLine && pkgLine.reactFee > 0 && (
                                <li className="flex items-baseline justify-between gap-2 text-[10px] text-amber-300">
                                    <span>(incl. reactivation fee)</span>
                                    <span className="font-mono">+{fmtMoney(pkgLine.reactFee)}</span>
                                </li>
                            )}
                        </ul>
                    )}
                    <div className="flex items-baseline justify-between gap-2 pt-2 border-t border-zinc-800">
                        <span className="text-[11px] tracking-[0.2em] uppercase text-zinc-400">Total</span>
                        <span
                            data-testid="casp-cart-total"
                            className="text-2xl font-mono font-bold text-white"
                        >
                            {fmtMoney(cartTotal)}
                        </span>
                    </div>
                    {!isComp && pkgLine && (
                        <div data-testid="casp-card-picker" className="mt-3">
                            <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-400 mb-1.5">
                                Card to charge
                            </div>
                            <CardSelector
                                userId={userId}
                                value={selectedPm}
                                onChange={setSelectedPm}
                                onLoaded={(list) => setCardCount(list.length)}
                            />
                            {cardCount === 0 && (
                                <p className="text-[10px] text-amber-300 mt-1.5 leading-snug">
                                    No card on file — add one in the Card-on-file panel above before charging.
                                </p>
                            )}
                        </div>
                    )}
                    <button
                        type="button"
                        disabled={busy || cartLines.length === 0 || (athletes.length > 0 && !selectedAthleteId)}
                        onClick={charge}
                        data-testid="casp-charge-cta"
                        className="w-full mt-3 px-4 py-3 bg-brand-red text-white text-[11px] tracking-[0.2em] uppercase font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                        {cartLines.length === 0
                            ? "Pick something to charge"
                            : athletes.length > 0 && !selectedAthleteId
                                ? "Pick an athlete first"
                                : isComp
                                    ? `Grant ${customSessionCount} free session${customSessionCount === 1 ? "" : "s"}`
                                    : `Charge ${fmtMoney(cartTotal)} now`}
                    </button>
                    {athletes.length === 0 && cartLines.length > 0 && (
                        <div
                            data-testid="casp-no-athletes-note"
                            className="text-[10px] text-zinc-500 mt-2 leading-snug"
                        >
                            No athletes linked to this profile yet — the charge will go to the family pool. You can tag an athlete record later.
                        </div>
                    )}
                    <div className="text-[10px] text-zinc-600 mt-2 leading-snug">
                        Tax is an estimate at the preview rate. The backend computes the exact rate at charge time.
                    </div>
                </div>
            </div>

            {/* Hard confirmation modal — replaces window.confirm. Gates
                $0 / large-discount charges behind a type-to-confirm token. */}
            <RiskyChargeConfirmModal
                open={!!pending}
                onClose={() => !busy && setPending(null)}
                onConfirm={commit}
                title={pending?.title || ""}
                athleteName={pending?.athleteName}
                lines={pending?.lines || []}
                basePriceCents={pending?.basePriceCents}
                effectivePriceCents={pending?.effectivePriceCents}
                interval={pending?.interval}
                discount={pending?.discount}
                footNote={pending?.footNote}
                busy={busy}
            />
        </div>
    );
}
